import type { ReactNode } from 'react';

/** Matches the `state` prop of the Notification atom. */
export type ToastState = 'success' | 'error' | 'warning' | 'information';

export interface Toast {
    id: string;
    message: ReactNode;
    state: ToastState;
    /** Auto-dismiss delay in ms. `0` means sticky — dismissed manually only. */
    duration: number;
}

export interface ToastOptions {
    state?: ToastState;
    duration?: number;
}

const DEFAULT_DURATION = 5_000;

/** Per-state defaults: transient states fade out, states the user must act on remain. */
const STATE_DEFAULTS: Record<ToastState, number> = {
    success: DEFAULT_DURATION,
    information: DEFAULT_DURATION,
    warning: 0,
    error: 0,
};

type Listener = () => void;

/**
 * A single module-level singleton that any module can import and call.
 */
class ToastStore {
    private toasts: Toast[] = [];
    private listeners: Set<Listener> = new Set();
    private timers: Map<string, ReturnType<typeof setTimeout>> = new Map();
    private nextId = 0;

    public subscribe = (listener: Listener): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    /* For React's `useSyncExternalStore` hook. */
    public getSnapshot = (): Toast[] => this.toasts;
    public getServerSnapshot = (): Toast[] => this.toasts;

    public show = (message: ReactNode, options: ToastOptions = {}): string => {
        const state = options.state ?? 'information';
        const duration = options.duration ?? STATE_DEFAULTS[state];
        const id = `toast-${this.nextId++}`;

        this.toasts = [...this.toasts, { id, message, state, duration }];
        this.emit();

        if (duration > 0) {
            this.timers.set(
                id,
                setTimeout(() => this.dismiss(id), duration)
            );
        }
        return id;
    };

    public dismiss = (id: string): void => {
        const timer = this.timers.get(id);
        if (timer) {
            clearTimeout(timer);
            this.timers.delete(id);
        }

        const next = this.toasts.filter((t) => t.id !== id);
        if (next.length !== this.toasts.length) {
            this.toasts = next;
            this.emit();
        }
    };

    private emit(): void {
        this.listeners.forEach((listener) => listener());
    }
}

const store = new ToastStore();

export const toastStore = store;

/**  API for triggering toasts from anywhere. */
export const toast = {
    show: (message: ReactNode, options?: ToastOptions) => store.show(message, options),
    dismiss: (id: string) => store.dismiss(id),
    success: (message: ReactNode, options?: ToastOptions) => store.show(message, { ...options, state: 'success' }),
    error: (message: ReactNode, options?: ToastOptions) => store.show(message, { ...options, state: 'error' }),
    warning: (message: ReactNode, options?: ToastOptions) => store.show(message, { ...options, state: 'warning' }),
    information: (message: ReactNode, options?: ToastOptions) =>
        store.show(message, { ...options, state: 'information' }),
};
